import { useI18n } from "@/i18n/I18nProvider";
import productsContent, { PRODUCTS } from "@/i18n/products";
import { ArrowUpRight, ArrowRight, ShieldCheck, Sparkles } from "lucide-react";
import Reveal, { fadeUp } from "@/components/motion/Reveal";

export default function Products() {
  const { locale } = useI18n();
  const p = productsContent[locale] || productsContent.en;

  return (
    <section id="products" data-testid="products-section" className="section-pad bg-white relative">
      <div className="absolute inset-0 noise-overlay pointer-events-none" aria-hidden />
      <div className="container-pro relative">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-12">
          <Reveal className="md:col-span-7">
            <div className="overline">{p.sectionKicker}</div>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight">
              {p.sectionTitle1}
              <br className="hidden md:block" />
              <span className="text-brand-red">{p.sectionTitle2}</span>
            </h2>
          </Reveal>
          <Reveal variants={fadeUp} className="md:col-span-5">
            <p className="text-ink-secondary">{p.sectionBody}</p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {PRODUCTS.map((slug, i) => (
            <Reveal key={slug} variants={fadeUp} className="h-full">
              <ProductCard product={p.catalog[slug]} common={p.common} />
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-10 flex justify-center">
          <a href="/products" className="btn-ghost" data-testid="products-section-cta">
            {p.sectionCta} <ArrowRight size={16} data-mirror-rtl="true" />
          </a>
        </Reveal>
      </div>
    </section>
  );
}

const ProductCard = ({ product, common }) => {
  const isTeal = product.accent === "teal";
  return (
    <article
      data-testid={`product-card-${product.slug}`}
      className={`group relative h-full overflow-hidden rounded-2xl border transition-all duration-300 hover:-translate-y-1 ${
        isTeal
          ? "bg-surface-dark text-white border-line-dark hover:shadow-[0_24px_64px_-16px_rgba(24,119,242,0.4)]"
          : "bg-surface-muted border-line hover:border-brand-red/40 hover:shadow-[0_24px_64px_-20px_rgba(24,119,242,0.3)]"
      } p-8 md:p-10`}
      style={isTeal ? { clipPath: "polygon(0 0, 100% 0, 100% calc(100% - 24px), calc(100% - 24px) 100%, 0 100%)" } : undefined}
    >
      {isTeal && <div className="absolute inset-0 dot-bg opacity-30 pointer-events-none" aria-hidden />}
      {/* spotlight from top */}
      <div
        className={`absolute -top-24 left-1/2 -translate-x-1/2 h-48 w-[130%] pointer-events-none blur-2xl transition-opacity duration-500 opacity-0 group-hover:opacity-100 ${
          isTeal ? "bg-brand-red/15" : "bg-brand-red/10"
        }`}
        aria-hidden
      />
      <div className="absolute top-0 end-0 h-20 w-20 ticker opacity-40 pointer-events-none" aria-hidden />

      <div className="relative">
        <div className="flex items-center justify-between">
          <ProductMark accent={product.accent} name={product.name} />
          <span className={`font-mono text-[10px] tracking-widest ${isTeal ? "text-white/40" : "text-ink-muted"}`}>
            / {product.version}
          </span>
        </div>

        <h3 className={`mt-7 font-heading text-2xl md:text-3xl font-medium tracking-tight leading-tight ${isTeal ? "text-white" : "text-ink-primary"}`}>
          {product.tagline}
        </h3>
        <p className={`mt-3 text-sm md:text-base leading-relaxed ${isTeal ? "text-white/70" : "text-ink-secondary"}`}>
          {product.short}
        </p>

        {/* Badges */}
        <div className="mt-6 flex flex-wrap gap-2">
          {product.badges.map((b) => (
            <span
              key={b}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest border ${
                isTeal ? "bg-white/5 text-white/80 border-white/15" : "bg-white text-ink-secondary border-line"
              }`}
            >
              {b}
            </span>
          ))}
        </div>

        {/* Modules */}
        <div className="mt-7 grid grid-cols-2 gap-3">
          {product.modules.map((m) => (
            <div key={m.code} className={`p-3 rounded-md border transition-colors ${isTeal ? "border-white/10 bg-white/[0.03] hover:border-brand-red/40" : "border-line bg-white hover:border-brand-red/40"}`}>
              <div className={`font-mono text-[10px] tracking-widest ${isTeal ? "text-white/40" : "text-ink-muted"}`}>{m.code}</div>
              <div className={`mt-1 text-sm font-semibold ${isTeal ? "text-white" : "text-ink-primary"}`}>{m.title}</div>
            </div>
          ))}
        </div>

        {/* CTA row */}
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <a
            href={product.url}
            target="_blank"
            rel="noreferrer noopener"
            data-testid={`product-${product.slug}-demo`}
            className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded transition-colors ${
              isTeal ? "bg-brand-red text-white hover:bg-brand-redHover" : "bg-brand-black text-white hover:bg-brand-red"
            }`}
          >
            {common.tryDemo} <ArrowUpRight size={14} />
          </a>
          <a
            href={`/products/${product.slug}`}
            data-testid={`product-${product.slug}-learn`}
            className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded border transition-colors ${
              isTeal ? "border-white/20 text-white hover:bg-white/10" : "border-ink-primary text-ink-primary hover:bg-ink-primary hover:text-white"
            }`}
          >
            {common.learnMore} <ArrowRight size={14} data-mirror-rtl="true" />
          </a>
        </div>
      </div>
    </article>
  );
};

const ProductMark = ({ accent, name }) => (
  <div className="flex items-center gap-3">
    <span className="relative inline-flex h-10 w-10 items-center justify-center">
      <span className={`absolute inset-0 rounded-md ${accent === "teal" ? "bg-brand-teal" : "bg-brand-black"}`} />
      <span className="absolute -end-1 -top-1 h-3.5 w-3.5 rounded-sm bg-brand-red" />
      <span className="relative">
        {accent === "teal" ? <Sparkles size={18} className="text-white" /> : <ShieldCheck size={18} className="text-white" />}
      </span>
    </span>
    <div>
      <div className={`font-heading text-lg font-semibold ${accent === "teal" ? "text-white" : "text-ink-primary"}`}>
        {name}
      </div>
      <div className={`font-mono text-[10px] uppercase tracking-[0.25em] ${accent === "teal" ? "text-white/40" : "text-ink-muted"}`}>
        Smart Terminals
      </div>
    </div>
  </div>
);
